import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Send, RefreshCw, Search } from "lucide-react";

const STATUS_STYLES = {
  sent: "bg-emerald-100 text-emerald-700",
  failed: "bg-red-100 text-red-700",
  disabled: "bg-slate-200 text-slate-600",
};
const STATUS_LABEL = { sent: "Gesendet", failed: "Fehlgeschlagen", disabled: "Deaktiviert (kein API-Key)" };
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function EmailLogPage() {
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (category) params.category = category;
      if (q) params.q = q;
      const { data } = await api.get("/admin/email-log", { params });
      setEntries(data.entries || []);
      setCategories(data.categories || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [status, category]);

  return (
    <div className="space-y-6" data-testid="email-log-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Send size={20} strokeWidth={1.5} /> E-Mail-Protokoll
        </h1>
        <p className="text-xs text-slate-500 max-w-2xl mt-1">
          Alle automatisch versendeten System-E-Mails (Einladungen, Erinnerungen, Berichte, Zuweisungen …) —
          zum Nachvollziehen, was wann an wen gesendet wurde, falls ein Kunde oder Coach anruft und sagt,
          er habe keine Mail bekommen.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-56 shrink-0">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10" />
          <input
            data-testid="email-log-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Empfänger suchen…"
            style={{ paddingLeft: "1.75rem" }}
            className="input-tech text-sm"
          />
        </div>
        <div className="w-44 shrink-0">
          <select data-testid="email-log-status-select" value={status} onChange={(e) => setStatus(e.target.value)} className="input-tech text-sm">
            <option value="">Alle Status</option>
            <option value="sent">Gesendet</option>
            <option value="failed">Fehlgeschlagen</option>
            <option value="disabled">Deaktiviert</option>
          </select>
        </div>
        <div className="w-52 shrink-0">
          <select data-testid="email-log-category-select" value={category} onChange={(e) => setCategory(e.target.value)} className="input-tech text-sm">
            <option value="">Alle Typen</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <button data-testid="email-log-refresh-button" onClick={load} className="px-3 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-100 flex items-center gap-2 shrink-0">
          <RefreshCw size={14} strokeWidth={1.5} /> Aktualisieren
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Lädt…</div>
      ) : entries.length === 0 ? (
        <div className="text-sm text-slate-500" data-testid="email-log-empty">Keine E-Mails gefunden.</div>
      ) : (
        <div className="border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="email-log-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-overline text-slate-500">
              <tr>
                <th className="px-4 py-3">Zeitpunkt</th>
                <th className="px-4 py-3">Empfänger</th>
                <th className="px-4 py-3">Betreff</th>
                <th className="px-4 py-3">Typ</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Lesebestätigung</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entries.map((e, i) => (
                <tr key={i} data-testid="email-log-row">
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fmtDateTime(e.sent_at)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{e.to}</div>
                    {e.recipient_name && <div className="text-xs text-slate-500">{e.recipient_name}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{e.subject}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{e.category}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[e.status] || "bg-slate-100 text-slate-600"}`} data-testid={`email-log-status-${e.status}`}>
                      {STATUS_LABEL[e.status] || e.status}
                    </span>
                    {e.error && <div className="text-xs text-red-500 mt-1 max-w-xs">{e.error}</div>}
                  </td>
                  <td className="px-4 py-3" data-testid={`email-log-read-receipt-${i}`}>
                    {e.category !== "invoice_leistungstraeger" ? (
                      <span className="text-slate-300 text-xs">—</span>
                    ) : e.read_receipt_received_at ? (
                      <span className="inline-block px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700">
                        Bestätigt am {fmtDateTime(e.read_receipt_received_at)}
                      </span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-500">
                        Noch keine Bestätigung
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
